import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { PageContainer, PageHeader, StatCard, EmptyState } from "@/components/Page";
import { StatusBadge, CategoryBadge } from "@/components/Badges";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function ManagerDashboard() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [units, setUnits] = useState([]);
  const [residents, setResidents] = useState([]);
  const [estate, setEstate] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [t, u, r, e] = await Promise.all([
          api.get("/tickets"),
          api.get("/units"),
          api.get("/users", { params: { role: "resident" } }),
          api.get("/estates"),
        ]);
        setTickets(t.data);
        setUnits(u.data);
        setResidents(r.data);
        setEstate(e.data[0] || null);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);

  const open = tickets.filter((t) => ["pending", "ongoing", "on_review"].includes(t.status)).length;
  const done = tickets.filter((t) => t.status === "complete").length;

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          eyebrow={estate ? estate.name : "Estate Manager"}
          title={`Hello, ${user?.name?.split(" ")[0] || "Manager"}.`}
          description="Your operational overview for the estate you manage."
        />

        {!user?.estate_id && (
          <div className="bg-amber-50 border border-amber-200 p-4 mb-6 text-sm text-amber-900" data-testid="no-estate-warning">
            You haven&apos;t been assigned to an estate yet. Contact your Super Admin.
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total Tickets" value={tickets.length} testid="stat-tickets" />
          <StatCard label="Open" value={open} testid="stat-open" hint="pending / ongoing / on review" />
          <StatCard label="Completed" value={done} testid="stat-complete" />
          <StatCard label="Residents" value={residents.length} testid="stat-residents" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
          <StatCard label="Units" value={units.length} testid="stat-units" />
          <StatCard label="Occupied Units" value={units.filter(u => u.resident_id).length} testid="stat-occupied" />
          <StatCard label="Vacant Units" value={units.filter(u => !u.resident_id).length} testid="stat-vacant" />
        </div>

        <div className="bg-white border border-zinc-200 overflow-x-auto">
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
            <div className="label-eyebrow">Recent Tickets</div>
            <Link to="/m/tickets" className="text-xs font-semibold uppercase tracking-wider underline underline-offset-4" data-testid="view-all-tickets-link">View all →</Link>
          </div>
          {tickets.length === 0 ? (
            <EmptyState title="No tickets yet" description="When residents raise tickets they'll show up here." />
          ) : (
            <table className="w-full text-sm min-w-[680px]">
              <thead className="border-b border-zinc-200">
                <tr className="text-left">
                  <th className="p-4 label-eyebrow font-semibold">Title</th>
                  <th className="p-4 label-eyebrow font-semibold">Category</th>
                  <th className="p-4 label-eyebrow font-semibold">Status</th>
                  <th className="p-4 label-eyebrow font-semibold">Raised By</th>
                </tr>
              </thead>
              <tbody>
                {tickets.slice(0, 8).map((t) => (
                  <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`ticket-row-${t.id}`}>
                    <td className="p-4">
                      <Link to={`/tickets/${t.id}`} className="font-semibold hover:underline underline-offset-4">{t.title}</Link>
                    </td>
                    <td className="p-4"><CategoryBadge category={t.category} /></td>
                    <td className="p-4"><StatusBadge status={t.status} /></td>
                    <td className="p-4 text-zinc-600">{t.raised_by?.name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </PageContainer>
    </AppShell>
  );
}
